/**
 * Sub-folder in Google Storage bucket https://github.com/RedTecLab/home-one-assets/blob/550f28c64cf54866cafda945efec86534508ea10/.circleci/config.yml#L56
 */
const BRAND_ILLUSTRATION_FOLDER = 'brand-illustrations'

/**
 * Image names as provided in https://github.com/RedTecLab/home-one-assets/tree/main/packages/brand-illustrations
 */
export type BrandIllustrationName =
  | 'account-security'
  | 'agent-centric-experience-design'
  | 'ai-readiness'
  | 'apps'
  | 'ar-and-vr'
  | 'austrian-flag'
  | 'austrian-flags'
  | 'austrian-heart-discount'
  | 'avatar-divers'
  | 'avatar-female'
  | 'avatar-male'
  | 'baby-family'
  | 'back-to-school'
  | 'beauty'
  | 'choose-an-image'
  | 'code-applied'
  | 'cologne'
  | 'complete-the-order'
  | 'completed'
  | 'confirmed'
  | 'consent'
  | 'copy-sale-code'
  | 'coupon-in-the-app'
  | 'coupon'
  | 'credit-balance-in-euro'
  | 'credit-balance-no-currency'
  | 'customer-support'
  | 'delivered'
  | 'diets-sports'
  | 'directions'
  | 'e-rezept-spaceholder'
  | 'easter-bunny'
  | 'easter-eggs'
  | 'employee-care'
  | 'empty-cart'
  | 'empty-inbox'
  | 'empty-order-list'
  | 'empty-payments'
  | 'empty-wishlist'
  | 'energetic'
  | 'expertise'
  | 'filled-cart'
  | 'filled-inbox'
  | 'filled-mailbox'
  | 'flexible-schedule'
  | 'forgotten-password'
  | 'free-delivery'
  | 'french-flag'
  | 'french-flags'
  | 'french-heart-discount'
  | 'growth'
  | 'hand-washing'
  | 'hashtag'
  | 'health'
  | 'insurance-number'
  | 'interaction-check'
  | 'italian-flags'
  | 'italian-heart-discount'
  | 'list-of-products'
  | 'make-a-selection'
  | 'maternity'
  | 'measure-eco'
  | 'medical-equipment'
  | 'medical-product'
  | 'medication-plan'
  | 'message'
  | 'missing-products'
  | 'movie-camera'
  | 'news'
  | 'newsletter'
  | 'nfc-and-checkmark'
  | 'nfc-and-nfc-sign'
  | 'notification'
  | 'now-delivery'
  | 'now-pick-up'
  | 'now'
  | 'ods-consultation'
  | 'ods'
  | 'offline-doctor'
  | 'offset-eco'
  | 'order-being-checked'
  | 'order-cancelled'
  | 'own-brands'
  | 'party-balloons'
  | 'passkey'
  | 'patient-care'
  | 'pets'
  | 'pharmacist'
  | 'pharmacy'
  | 'pick-up'
  | 'piggy-bank-with-chf'
  | 'piggy-bank-with-euro'
  | 'planetary-care'
  | 'pregnancy'
  | 'present'
  | 'product-bundle'
  | 'product-range'
  | 'ratings'
  | 'red-points'
  | 'redirect'
  | 'redpoints-redeemed'
  | 'reduce-eco'
  | 'repeat-prescription'
  | 'rx'
  | 'sale-code'
  | 'scan-a-product'
  | 'scan-qr-code-on-mobile'
  | 'scan-qr-code'
  | 'social-security'
  | 'sport'
  | 'standard-delivery'
  | 'sun'
  | 'survey'
  | 'sustainable-product'
  | 'swap'
  | 'swiss-flags'
  | 'swiss-sign-discount'
  | 'tracking'
  | 'under-construction'
  | 'unknown-location'
  | 'update'
  | 'vegan-cosmetics'
  | 'vegan-products'
  | 'waiting-for-payment-in-chf'
  | 'waiting-for-payment-in-euro'
  | 'web-camera'
  | 'winter'

/*
 * Until we have Celum integration we provide scales only for images
 * we upload manually to Google Storage bucket
 */
type BrandIllustrationScale = 1 | 2

// eslint-disable-next-line perfectionist/sort-union-types -- semantic order
export type BrandIllustrationSize = 's' | 'm'

export type BrandIllustrationType = 'clear' | 'dark' | 'light'

/**
 * `clear` brand illustration have square aspect ratio, while `dark` and `light` have 9:8
 */
const variantSizes: Record<
  `${BrandIllustrationSize}-${BrandIllustrationType}`,
  {
    height: number
    width: number
  }
> = {
  'm-clear': { height: 144, width: 144 },
  'm-dark': { height: 144, width: 162 },
  'm-light': { height: 144, width: 162 },
  's-clear': { height: 96, width: 96 },
  's-dark': { height: 96, width: 108 },
  's-light': { height: 96, width: 108 },
}

/**
 * Generates brand illustration image URL & provide the fixed size for it
 *
 * @example
 * ```json
 *  {
 *    height: 144,
 *    src: https://cdn.ref-sa-tech.io/home-one-assets/brand-illustrations/empty-cart-m-dark-2x.png,
 *    width: 162
 *  }
 * ```
 */
export const brandIllustrationGetImage = ({
  cdnUrl,
  name,
  scale = 1,
  size,
  type,
}: {
  cdnUrl: string
  name: BrandIllustrationName
  scale?: BrandIllustrationScale
  size: BrandIllustrationSize
  type: BrandIllustrationType
}): {
  height: number
  src: string
  width: number
} => {
  let fileName = `${name}-${size}-${type}`

  if (scale > 1) {
    fileName = `${fileName}-${scale}x`
  }

  return {
    ...variantSizes[`${size}-${type}`],
    src: `${cdnUrl}${BRAND_ILLUSTRATION_FOLDER}/${fileName}.png`,
  }
}
